import type { Escala, Pregunta } from '@copathy/shared';
import type { Acento } from './PreguntaRespondible';
import {
  abreEnPantallaCompleta,
  estaRespondida,
  resumenDeRespuesta,
  type RespuestaLocal,
} from './respuestaLocal';
import { Check, Flecha } from './iconos';

/**
 * Una pregunta **plegada** en la lista del recorrido (CC-12, D-140).
 *
 * ── Qué tiene que hacer una fila plegada, y por qué es lo difícil ──
 * Tres cosas a la vez, en una línea de teléfono:
 *   1. Decir **qué pregunta es** —el enunciado, cortado si hace falta—.
 *   2. Decir **si ya se respondió**, de un golpe y sin leer.
 *   3. Decir **qué se respondió**, para poder revisar sin abrir nada.
 *
 * La tercera es la que se gana o se pierde: si el resumen no cabe o no dice nada,
 * plegar **esconde** la respuesta en vez de mostrarla, y entonces la lista es
 * peor que tenerlo todo desplegado. El resumen por tipo vive en
 * `respuestaLocal.ts` con el razonamiento de cada uno.
 *
 * ⚠️ **El estado «respondida» se marca con un visto Y con color**, no solo con
 * color: en una lista de veinte, distinguir «hecha» de «pendiente» por un tono de
 * gris es pedirle al ojo lo que no puede dar — y a quien no distingue esos tonos,
 * imposible.
 */
export function FilaPlegada({
  pregunta,
  numero,
  respuesta,
  escala,
  acento,
  resaltada,
  alAbrir,
}: {
  pregunta: Pregunta;
  numero: number;
  respuesta: RespuestaLocal | undefined;
  escala: Escala | undefined;
  acento: Acento;
  /** La que se está editando en el constructor. */
  resaltada?: boolean;
  alAbrir: () => void;
}) {
  const hecha = estaRespondida(pregunta, respuesta);
  const resumen = resumenDeRespuesta(pregunta, respuesta, escala);
  const pantallaCompleta = abreEnPantallaCompleta(pregunta);
  const conObservacion = (respuesta?.observacion ?? '').trim().length > 0;

  return (
    <button
      type="button"
      onClick={alAbrir}
      className="flex w-full items-start gap-3 rounded-2xl border bg-white p-3.5 text-left
        transition hover:bg-marino-50/50
        focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
      style={{
        borderColor: resaltada ? acento.fuerte : '#e4ebf3',
        boxShadow: resaltada ? `0 0 0 4px ${acento.suave}` : undefined,
      }}
    >
      {/*
        El número, que pasa a visto cuando está hecha. Es el mismo sitio, así que
        el ojo no tiene que buscar en dos lugares para saber si falta algo.
      */}
      <span
        className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-sm font-bold"
        style={
          hecha
            ? { background: acento.fuerte, color: acento.textoSobreFuerte }
            : { background: '#eef3f9', color: '#5b7391' }
        }
      >
        {hecha ? <Check size={15} /> : numero}
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={`block text-sm leading-snug ${
            hecha ? 'font-medium text-marino-500' : 'font-semibold text-marino-900'
          }`}
        >
          {pregunta.texto || 'Sin enunciado todavía'}
          {pregunta.obligatoria && !hecha && (
            <span className="ml-1" style={{ color: acento.fuerte }}>
              *
            </span>
          )}
        </span>

        {/*
          La respuesta, o la invitación a darla. Nunca las dos, y nunca ninguna:
          una fila sin segunda línea salta de alto respecto a las demás y la lista
          se ve desalineada.
        */}
        {resumen ? (
          <span className="mt-1 flex items-baseline gap-1.5">
            <span
              className="min-w-0 flex-1 truncate text-sm font-semibold"
              style={{ color: acento.fuerte }}
            >
              {resumen}
            </span>
          </span>
        ) : (
          <span className="mt-1 block text-xs text-marino-400">
            {pantallaCompleta ? 'Toca para responder' : 'Sin responder'}
          </span>
        )}

        {/*
          Que dejó un comentario se dice, pero NO se muestra: el texto puede ser
          largo y sobre su jefe, y una lista que lo enseña de paso lo pone a la
          vista de quien mire el teléfono por encima del hombro.
        */}
        {conObservacion && (
          <span className="mt-1 block text-xs text-marino-400">Con comentario</span>
        )}
      </span>

      {/*
        `Flecha` apunta a la izquierda —es la de «volver»— así que se gira. Un
        icono propio para esto sería un séptimo `path` en `iconos.tsx` que dice
        lo mismo.
      */}
      <span
        aria-hidden="true"
        className="mt-0.5 shrink-0 rotate-180 text-marino-300"
        title={pantallaCompleta ? 'Se abre en pantalla completa' : 'Se despliega acá'}
      >
        <Flecha size={16} />
      </span>
    </button>
  );
}
